import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useLocation } from "react-router-dom";

import {
  Directions,
  sportConstantsReversed,
  timezoneConstants,
  translationConstants,
} from "../../constants";
import { centerInstructorsActions } from "../../redux/actions";
import {
  PhoneIcon,
  MessageIcon,
  BoatIcon,
  StudentIcon,
  HoursIcon,
} from "../../assets/images/icon/iconsProvider";
import {
  appendMandatorySignAndTimezoneText,
  serverFormatToDateString,
} from "../../system";
import {
  ProfilePlaceholder,
  RemoveInstructorIcon,
} from "../../assets/images/icon";

import ReviewStars from "./ReviewStars/ReviewStars";
import CenterInfoCard from "./ViewCenterCard/CenterInfoCard";
import ShowUserLevels from "./ShowUserLevels/ShowUserLevels";

const InstructorDetail = (props) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [instructorData, setInstructorData] = useState();

  const direction = useSelector((state) => {
    return state.LanguageChangeReducer.direction;
  });
  const instructorDetails = useSelector(
    (state) => state.centerInstructors?.centerInstructorDetails
  );

  const joinedSportNames = [
    ...new Set(
      instructorDetails?.sportLevels?.map((item) =>
        t(translationConstants[sportConstantsReversed[item.sportId]])
      )
    ),
  ].join(", ");

  const { state } = useLocation();

  const userCenterId = useSelector(
    (state) => state.LoginReducer.UserData?.user?.centerId
  );

  const centerId = props.isGuestUser ? state.centerId : userCenterId;

  useEffect(() => {
    dispatch(
      centerInstructorsActions.getCenterInstructorDetailById(
        props.instructorData?.instructorId,
        props.centerId !== undefined
          ? props.centerId
          : props.isFreelanceInstructor
          ? ""
          : centerId
          ? centerId
          : ""
      )
    );
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    setInstructorData(instructorDetails);
  }, [instructorDetails]);

  return (
    <div className="row p-3 px-0 px-md-3">
      <div className="col-12 col-lg-8">
        <div className="row">
          <div className="col-4 col-md-2">
            <img
              src={
                instructorData?.profilePicture?.link
                  ? instructorData?.profilePicture?.link
                  : ProfilePlaceholder
              }
              alt="Instructor Profile"
              className="instructor-profile-image rounded-circle"
            />
          </div>

          <div className="col-8 col-md-10">
            <div className="instructor-name-wrapper d-flex align-items-md-center flex-column flex-md-row gap-1 gap-md-5">
              <span>
                {direction === Directions.rtl && instructorData?.arabicFirstName
                  ? `${instructorData?.arabicFirstName} ${instructorData?.arabicLastName}`
                  : `${instructorData?.englishFirstName} ${instructorData?.englishLastName}`}
              </span>
              <div className="d-flex align-items-md-center flex-column flex-md-row gap-2 gap-md-3">
                <div>
                  <ReviewStars value={instructorData?.rating} size={20} />{" "}
                </div>
                <span className="font-14">
                  {t(translationConstants.OUT_OF)}{" "}
                  {instructorData?.noOfReviews ?? 0}{" "}
                  {t(translationConstants.REVIEWS)}
                </span>
              </div>
            </div>
            <div className="font-14 fw-500">
              <div className="contact-information-card pt-2 d-flex gap-2 align-items-center">
                <PhoneIcon />
                <span className="font-14 fw-500" dir={Directions.ltr}>
                  {instructorData?.phoneNumber}
                </span>
              </div>
              <div className="contact-information-card pt-2 d-flex gap-2 align-items-center">
                <MessageIcon />
                <span className="font-14 fw-500">{instructorData?.email}</span>
              </div>
            </div>
          </div>
        </div>

        <div className="row pt-4 px-1 justify-content-center justify-content-md-start">
          <div className="col-5 col-md-2 mt-2 px-1">
            <CenterInfoCard
              IconImage={BoatIcon}
              label={translationConstants.SPORTS}
              value={instructorDetails?.noOfSports ?? 0}
            />
          </div>
          <div className="col-5 col-md-2 mt-2 px-1">
            <CenterInfoCard
              IconImage={StudentIcon}
              label={translationConstants.STUDENTS}
              value={instructorDetails?.noOfStudents ?? 0}
            />
          </div>
          <div className="col-5 col-md-2 mt-2 px-1">
            <CenterInfoCard
              IconImage={HoursIcon}
              label={translationConstants.HOURS}
              value={instructorDetails?.noOfHours ?? 0}
            />
          </div>
        </div>

        <div className="row">
          {props.isShowRemoveButton && (
            <div className="d-flex align-items-center my-2 justify-content-end">
              <button
                className="disable-button mx-1 my-20"
                type="button"
                onClick={() => {
                  props.onDelete(props.instructorData);
                }}
              >
                <img
                  src={RemoveInstructorIcon}
                  name="rejected"
                  height={18}
                  alt=""
                  className="pe-1"
                />
                {t(translationConstants.REMOVE)}
              </button>
            </div>
          )}
        </div>
        <div className="row p-4 pt-3 px-0 px-md-4">
          <div className="col-12 col-sm-6 d-flex flex-column gap-1 py-2">
            <span className="second-card-heading-text ">
              {instructorDetails?.joiningDateString &&
                appendMandatorySignAndTimezoneText(
                  t(translationConstants.JOINING_DATE),
                  t,
                  false
                )}
            </span>
            <span className="second-card-description-text">
              {serverFormatToDateString(
                instructorDetails?.joiningDateString,
                timezoneConstants.dayMonthYearFormat
              )}
            </span>
          </div>
          <div className="col-12 col-sm-6 d-flex flex-column gap-1 py-2">
            <span className="second-card-heading-text ">
              {t(translationConstants.SPORTS_OFFERED)}
            </span>
            <span className="second-card-description-text">
              {joinedSportNames}
            </span>
          </div>
        </div>

        <div className="row p-4 pt-0 px-0 px-md-4">
          <div className="d-flex flex-column gap-2">
            <span className="second-card-heading-text">
              {t(translationConstants.INSTRUCTOR_LEVEL)}
            </span>
            <div className="row">
              <ShowUserLevels
                showIcon={true}
                columnClasses="col-12 col-sm-6 mb-3"
                headingClass="second-card-heading-text"
                listItemClass="second-card-description-text"
                userLevels={instructorDetails?.sportLevels}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default InstructorDetail;
